import type {SmmConnectionState,SmmPendingIntent} from './product-types';
import {useEffect,useRef,useState} from 'react';
import {smmStage4TenderLabel} from './stage4-checkout.mjs';
import {
  smmStage5ConfirmedDisplayCode,
  smmStage5RepairPath,
  smmStage5SharedState,
  smmStage5SubmissionShortRef,
} from './stage5-submit.mjs';

export type SmmStage5State='DRAFT'|'PENDING'|'CONFIRMED'|'REJECTED'|'UNKNOWN';

export function internetOrderRejectionReason(message?:string){
  const code=String(message??'').toUpperCase();
  if(/SCHEMA|FORMAT|VARIATION_UNSUPPORTED/.test(code))return '訂單格式不被接受，請返回檢查餐點選項。';
  if(/PRICE|FACT_MISMATCH|MENU|PROOF|HASH|IDENTITY|CHECKPOINT|PROJECTION/.test(code))return '餐單或價錢資料已更新，請重新核對餐點及總額。';
  if(/OPTION|COMBO|UNAVAILABLE|COUNT_INVALID/.test(code))return '餐點或選項暫不可用，請返回重新選擇。';
  if(/STAFF|UNAUTHORIZED/.test(code))return '員工身份未能確認，請重新登入。';
  if(/CONFLICT/.test(code))return '這次提交的資料不一致，請先核對原訂單結果。';
  return '門店未能接受今次訂單，請返回檢查。';
}

export interface SmmStage5Session{
  readonly intent:SmmPendingIntent;
  readonly state:SmmStage5State;
  readonly displayCode?:string;
  readonly message?:string;
}

function Stage5SharedState({connection}:{connection:SmmConnectionState}){
  const shared=smmStage5SharedState(connection,true);
  if(!shared||shared.kind==='EMPTY')return null;
  return <section className={`stage5-shared stage5-shared-${shared.kind.toLowerCase()}`} data-stage5-shared-state={shared.kind} role="status">
    <span className="stage5-shared-icon" aria-hidden="true">{shared.icon}</span>
    <div><strong>{shared.label}</strong><small>{shared.detail}</small></div>
  </section>;
}

function Stage5TopBar({title,onBack}:{title:string;onBack:()=>void}){
  return <header className="stage5-topbar">
    <button type="button" className="stage5-back" onClick={onBack} aria-label="返回">‹</button>
    <strong>{title}</strong>
    <span className="stage5-topbar-spacer" aria-hidden="true"/>
  </header>;
}

const STAGE5_ART=Object.freeze({
  pending:'/brand/stage5/stage5-pending.webp',
  rejected:'/brand/stage5/stage5-rejected.webp',
});

function Stage5Artwork({name,alt}:{name:keyof typeof STAGE5_ART;alt:string}){
  return <img className={`stage5-art stage5-art-${name}`} src={STAGE5_ART[name]} alt={alt}/>;
}

function Stage5Submitting({session}:{session:SmmStage5Session}){
  const shortRef=smmStage5SubmissionShortRef(session.intent.submissionId);
  return <section className="stage5-state stage5-submitting" data-stage5-visual="5.2_SUBMITTING">
    <div className="v3-submit-ring" role="progressbar" aria-label="落緊單"/>
    <h1>送緊去收銀機…</h1>
    <div className="stage5-progress-list" aria-live="polite">
      <div className="done"><i>✓</i><span>檢查好內容同價錢</span></div>
      <div className="active"><i>●</i><span>送出訂單</span></div>
      <div className="waiting"><i/><span>等收銀機確認</span></div>
    </div>
    <p className="stage5-foot-ref">提交編號 <b>{shortRef}</b>・唔使再撳</p>
  </section>;
}

function Stage5Pending({session,onPendingDetails,onHome}:{session:SmmStage5Session;onPendingDetails:()=>void;onHome:()=>void}){
  const shortRef=smmStage5SubmissionShortRef(session.intent.submissionId);
  const submittedAt=new Date(session.intent.updatedAt);
  const time=Number.isFinite(submittedAt.getTime())
    ?submittedAt.toLocaleString('zh-HK',{hour12:false,month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'})
    :'剛剛';
  return <section className="stage5-state stage5-pending" data-stage5-visual="5.3_PENDING">
    <span className="stage5-badge pending">等待門店確認</span>
    <Stage5Artwork name="pending" alt="訂單已提交"/>
    <h1>已送出訂單</h1>
    <p>等收銀機確認。</p>
    <div className="stage5-fact-card">
      <div><span>提交時間</span><strong>{time}</strong></div>
      <div><span>來源</span><strong>手機點單</strong></div>
      <div><span>提交編號</span><strong>{shortRef}</strong></div>
    </div>
    <div className="stage5-actions">
      <button className="primary stage5-primary" type="button" onClick={onPendingDetails}>查看訂單詳情</button>
      <button className="stage5-secondary" type="button" onClick={onHome}>返回主頁</button>
    </div>
  </section>;
}

function Stage5Confirmed({session,onViewOrder,onContinue,diningLabel}:{session:SmmStage5Session;onViewOrder:()=>void;onContinue:()=>void;diningLabel?:string}){
  const [seconds,setSeconds]=useState(5);
  const [paused,setPaused]=useState(false);
  const continueRef=useRef(onContinue);
  continueRef.current=onContinue;
  useEffect(()=>{
    if(paused)return;
    if(seconds===0){continueRef.current();return;}
    const timer=window.setTimeout(()=>setSeconds(value=>value-1),1000);
    return()=>window.clearTimeout(timer);
  },[seconds,paused]);
  const displayCode=smmStage5ConfirmedDisplayCode(session.displayCode)??'—';
  const target=session.intent.checkout.diningTarget;
  const dining=session.intent.checkout.serviceMode==='DINE_IN'
    ?target?.kind==='TABLE'?`${diningLabel||'枱號待讀回'}・堂食`:'輪候・堂食'
    :'外賣';
  const count=session.intent.cart.reduce((sum,line)=>sum+line.quantity,0);
  const amount=new Intl.NumberFormat('zh-HK',{style:'currency',currency:'HKD',currencyDisplay:'narrowSymbol',minimumFractionDigits:session.intent.publishedTotalMinor%100===0?0:2,maximumFractionDigits:2}).format(session.intent.publishedTotalMinor/100);
  const tender=session.intent.checkout.tender;
  return <section className="stage5-state stage5-confirmed" data-stage5-visual="5.4_CONFIRMED">
    <header className="v3-result-hero"><span className="v3-result-icon" aria-hidden="true">✓</span><h1>收銀機已收單</h1><span>取餐碼</span><strong className="v3-pickup-code">{displayCode}</strong></header>
    <div className="stage5-confirm-card">
      <div><span>方式</span><strong>{dining}</strong></div>
      <div><span>付款方式</span><strong>{session.intent.checkout.serviceMode==='DINE_IN'?'食完先埋單':tender?smmStage4TenderLabel(tender):'未有資料'}</strong></div>
      <div><span>內容</span><strong>{count} 件</strong></div>
      <div><span>合計</span><strong>{amount}</strong></div>
    </div>
    <div className="stage5-actions">
      <div className="v3-result-secondary"><button className="stage5-secondary" type="button" onClick={onViewOrder}>睇訂單</button><button className="stage5-secondary" type="button" onClick={()=>setPaused(value=>!value)}>{paused?'繼續倒數':'停喺呢頁'}</button></div>
      <button className="primary stage5-primary" type="button" onClick={onContinue}>繼續點單{paused?'':'・'+seconds+' 秒'}</button>
    </div>
  </section>;
}

function Stage5Rejected({session,onRepair,onBack}:{session:SmmStage5Session;onRepair:()=>void;onBack:()=>void}){
  const repair=smmStage5RepairPath(session.message);
  return <section className="stage5-state stage5-rejected" data-stage5-visual="5.5_REJECTED">
    <Stage5Artwork name="rejected" alt="訂單未能提交"/>
    <h1>未能提交訂單</h1>
    <p>{internetOrderRejectionReason(session.message)}正式訂單未建立。</p>
    <div className="stage5-reason-card" role="alert">
      <strong>原因</strong>
      <ul><li>{repair.title}</li><li>{repair.detail}</li></ul>
    </div>
    <div className="stage5-actions">
      <button className="stage5-danger" type="button" onClick={onRepair}>返回修改</button>
      <button className="stage5-secondary" type="button" onClick={onBack}>查看餐單版本</button>
    </div>
  </section>;
}

function Stage5Unknown({session,reading,onReadback,onViewOrder,onContinue,onNewOrder}:{session:SmmStage5Session;reading:boolean;onReadback:()=>void;onViewOrder:()=>void;onContinue:()=>void;onNewOrder?:()=>void}){
  const count=session.intent.cart.reduce((sum,line)=>sum+line.quantity,0);
  const amount=new Intl.NumberFormat('zh-HK',{style:'currency',currency:'HKD',currencyDisplay:'narrowSymbol',minimumFractionDigits:session.intent.publishedTotalMinor%100===0?0:2,maximumFractionDigits:2}).format(session.intent.publishedTotalMinor/100);
  const submitted=new Date(session.intent.updatedAt);
  return <section className="stage5-state stage5-unknown" data-stage5-visual="5.6_UNKNOWN">
    <header className="v3-result-hero"><span className="v3-result-icon" aria-hidden="true">?</span><h1>未確認收銀機有冇收到</h1><p>唔好重新落單，先撳下面確認。</p></header>
    <div className="stage5-fact-card">
      <div><span>提交編號</span><strong>{smmStage5SubmissionShortRef(session.intent.submissionId)}</strong></div>
      <div><span>送出時間</span><strong>{Number.isFinite(submitted.getTime())?submitted.toLocaleTimeString('zh-HK',{hour:'2-digit',minute:'2-digit',hour12:false}):'未有資料'}</strong></div>
      <div><span>內容</span><strong>{count} 件・{amount}</strong></div>
      <div><span>購物車</span><strong>保留住</strong></div>
    </div>
    <div className="stage5-actions">
      <div className="v3-result-secondary"><button className="stage5-secondary" type="button" disabled={reading} onClick={onViewOrder}>去訂單頁睇</button><button className="stage5-secondary" type="button" disabled={reading} onClick={onContinue}>遲啲先確認</button></div>
      <button className="primary stage5-primary" type="button" disabled={reading} onClick={onReadback}>{reading?'正在確認原結果':'再確認一次'}</button>
      {onNewOrder?<button className="stage5-secondary" type="button" disabled={reading} onClick={onNewOrder}>保留原單，另開新單</button>:null}
    </div>
  </section>;
}

export function Stage5SubmitView({
  session,
  submitting,
  reading,
  connection,
  onReadback,
  onRepair,
  onBack,
  onViewOrder,
  onContinue,
  onNewOrder,
  onPendingDetails,
  onHome,
  diningLabel,
}:{
  session:SmmStage5Session;
  submitting:boolean;
  reading:boolean;
  connection:SmmConnectionState;
  onReadback:()=>void;
  onRepair:()=>void;
  onBack:()=>void;
  onViewOrder:()=>void;
  onContinue:()=>void;
  onNewOrder?:()=>void;
  onPendingDetails:()=>void;
  onHome:()=>void;
  diningLabel?:string;
}){
  const title=
    session.state==='DRAFT'&&submitting?'提交中':
    session.state==='DRAFT'?'尚未送出':
    session.state==='PENDING'?'已提交':
    session.state==='CONFIRMED'?'訂單確認':
    session.state==='REJECTED'?'提交失敗':
    '狀態未明';
  const sharedState=smmStage5SharedState(connection,true);

  return <div
    className="stage5-screen"
    data-stage5-state={session.state}
    data-stage5-transport-state={sharedState?.kind??'READY'}
    aria-label="正式提交訂單"
  >
    <Stage5TopBar title={title} onBack={onBack}/>
    <Stage5SharedState connection={connection}/>
    <main className="stage5-body">
      {session.state==='DRAFT'&&submitting?<Stage5Submitting session={session}/>:null}
      {session.state==='PENDING'?<Stage5Pending session={session} onPendingDetails={onPendingDetails} onHome={onHome}/>:null}
      {session.state==='CONFIRMED'?<Stage5Confirmed session={session} onViewOrder={onViewOrder} onContinue={onContinue} diningLabel={diningLabel}/>:null}
      {session.state==='REJECTED'?<Stage5Rejected session={session} onRepair={onRepair} onBack={onBack}/>:null}
      {session.state==='UNKNOWN'?<Stage5Unknown session={session} reading={reading} onReadback={onReadback} onViewOrder={onViewOrder} onContinue={onContinue} onNewOrder={onNewOrder}/>:null}
    </main>
  </div>;
}
